/**
 * Legacy Adapter — converts old single-intent JSON into pipeline arrays.
 *
 * During migration, the intent parser continues to produce legacy intents
 * (e.g., { type: 'navigation', target: { location: 'France' } }).
 * This adapter converts them into single-step pipeline arrays that the
 * pipeline executor can process.
 *
 * Once all handlers are extracted into ops/ modules and the system prompt
 * is updated (Phase 4), Claude will emit pipeline arrays directly and
 * this adapter becomes the fallback for backward compatibility.
 *
 * Phase 1: Only 'navigate' and 'terrain' are converted to pipelines.
 * Phase 2: ALL intent types now have pipeline conversions.
 *          The only intents that fall through are 'educational' (handled
 *          by main.js streaming) and truly unknown formats.
 * Phase 3: Composition — combined boundaries+cities detection produces
 *          multi-step pipelines: [boundaries, overlay_cities].
 */

/**
 * Detect if an intent is a combined boundary + city request.
 * (e.g., "Show states and capitals of USA", "Show me provinces and cities of France")
 *
 * Ported from QueryRouter._isCombinedBoundaryAndCities (lines 719-734).
 * When detected, the adapter produces a 2-step composed pipeline instead
 * of a single boundaries step.
 *
 * @param {object} intent — parsed intent
 * @returns {boolean}
 */
function _isCombinedBoundaryAndCities(intent) {
  // Never intercept POI queries (places dataset)
  if (intent.target?.dataset === 'places') return false

  const text = [
    intent.speech || '',
    intent.voiceResponse?.template || '',
    intent.original_query || intent.query || '',
  ]
    .join(' ')
    .toLowerCase()

  const hasBoundaries = /states?|provinces?|boundar|regions?|divisions?|territories/.test(text)
  const hasCities = /capital|cities|city|towns?/.test(text)
  const hasCountry = !!(intent.target?.location || intent.target?.country || intent.country)

  return hasBoundaries && hasCities && hasCountry
}

/**
 * Attempt to convert a legacy intent into a pipeline array.
 *
 * @param {object} intent — parsed intent from intent-parser.js
 * @returns {{ pipeline: object[], handled: true } | { handled: false }}
 *
 * Returns { handled: true, pipeline: [...] } if this intent can be
 * processed by the pipeline executor.
 * Returns { handled: false } if the intent should fall through to
 * the legacy QueryRouter.route() method.
 */
export function adaptIntent(intent) {
  if (!intent) {
    return { handled: false }
  }

  // If the intent already IS a pipeline (Phase 4+), pass it through
  if (Array.isArray(intent.pipeline)) {
    return { handled: true, pipeline: intent.pipeline }
  }

  // Legacy intents require a type field
  if (!intent.type) {
    return { handled: false }
  }

  // ── Phase 3: Composition detection (before switch) ────────────
  // Detect combined boundary+city requests from ANY intent type.
  // Produces a 2-step pipeline: boundaries → overlay_cities.
  if (_isCombinedBoundaryAndCities(intent)) {
    const country = intent.country || intent.target?.country || null
    const location = intent.target?.location || null
    return {
      handled: true,
      pipeline: [
        {
          op: 'boundaries',
          action: intent.action || 'show_divisions',
          country,
          subtype: intent.subtype || null,
          name: intent.name || null,
          speech: intent.speech || null,
          admin_level: intent.admin_level || null,
          show_capitals: false, // cities handled by next step
        },
        {
          op: 'overlay_cities',
          country,
          location,
        },
      ],
    }
  }

  switch (intent.type) {
    // ── Phase 1: Camera and terrain operations ─────────────────
    case 'navigation':
      return {
        handled: true,
        pipeline: [
          {
            op: 'navigate',
            location: intent.target?.location || intent.target?.country || null,
            coordinates: intent.target?.coordinates || null,
          },
        ],
      }

    case 'terrain':
      return {
        handled: true,
        pipeline: [
          {
            op: 'terrain',
            action: intent.action || 'toggle',
            exaggeration: intent.exaggeration ?? null,
          },
        ],
      }

    case 'projection':
      return {
        handled: true,
        pipeline: [{ op: 'projection', mode: intent.mode || 'globe' }],
      }

    case 'compass':
      return {
        handled: true,
        pipeline: [{ op: 'compass', action: intent.action || 'on' }],
      }

    case 'graticule':
      return {
        handled: true,
        pipeline: [{ op: 'graticule', action: intent.action || 'on' }],
      }

    case 'neighbors':
      return {
        handled: true,
        pipeline: [
          {
            op: 'neighbors',
            country: intent.country || null,
            iso_code: intent.iso_code || null,
          },
        ],
      }

    case 'point_in_region':
      return {
        handled: true,
        pipeline: [
          {
            op: 'point_in_region',
            target: intent.target || null,
            region: intent.region || null,
            region_class: intent.region_class || null,
            point_attribute: intent.point_attribute || null,
            point_operator: intent.point_operator || null,
            point_value: intent.point_value != null ? intent.point_value : null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'buffer_zone':
      return {
        handled: true,
        pipeline: [
          {
            op: 'buffer_zone',
            target: intent.target || null,
            radius_km: intent.radius_km != null ? intent.radius_km : null,
            location: intent.location || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'clip_to_region':
      return {
        handled: true,
        pipeline: [
          {
            op: 'clip_to_region',
            target: intent.target || null,
            region: intent.region || intent.location || null,
            admin_level: intent.admin_level || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'convex_hull':
      return {
        handled: true,
        pipeline: [
          {
            op: 'convex_hull',
            target: intent.target || null,
            location: intent.location || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'voronoi':
      return {
        handled: true,
        pipeline: [
          {
            op: 'voronoi',
            target: intent.target || null,
            location: intent.location || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'centroid':
      return {
        handled: true,
        pipeline: [
          {
            op: 'centroid',
            target: intent.target || 'countries',
            location: intent.location || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'dissolve':
      return {
        handled: true,
        pipeline: [
          {
            op: 'dissolve',
            target: intent.target || 'countries',
            location: intent.location || null,
            groupBy: intent.groupBy || intent.group_by || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'union':
      return {
        handled: true,
        pipeline: [
          {
            op: 'union',
            target: intent.target || 'countries',
            regionA: intent.regionA || intent.region_a || null,
            regionB: intent.regionB || intent.region_b || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'erase':
      return {
        handled: true,
        pipeline: [
          {
            op: 'erase',
            target: intent.target || 'countries',
            regionA: intent.regionA || intent.region_a || null,
            regionB: intent.regionB || intent.region_b || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    case 'intersect':
      return {
        handled: true,
        pipeline: [
          {
            op: 'intersect',
            target: intent.target || 'countries',
            regionA: intent.regionA || intent.region_a || null,
            operandB: intent.operandB || intent.operand_b || null,
            speech: intent.speech || intent.voice_response || null,
          },
        ],
      }

    // ── Phase 2: All core handler extractions ──────────────────

    case 'show':
      return {
        handled: true,
        pipeline: [
          {
            op: 'show',
            dataset: intent.target?.dataset || 'countries',
            location: intent.target?.location || null,
            country: intent.target?.country || null,
            coordinates: intent.target?.coordinates || null,
            attribute: intent.operation?.attribute || 'NAME',
          },
        ],
      }

    case 'choropleth':
      return {
        handled: true,
        pipeline: [
          {
            op: 'choropleth',
            dataset: intent.target?.dataset || 'countries',
            location: intent.target?.location || null,
            country: intent.target?.country || null,
            coordinates: intent.target?.coordinates || null,
            attribute: intent.operation?.attribute || 'POP_EST',
            colorRamp: intent.visualization?.colorRamp || 'YlOrRd',
            colorScheme: intent.visualization?.colorScheme || 'sequential',
          },
        ],
      }

    case 'analyze':
      // analyze is a pass-through to choropleth
      return {
        handled: true,
        pipeline: [
          {
            op: 'choropleth',
            dataset: intent.target?.dataset || 'countries',
            location: intent.target?.location || null,
            country: intent.target?.country || null,
            coordinates: intent.target?.coordinates || null,
            attribute: intent.operation?.attribute || 'POP_EST',
            colorRamp: intent.visualization?.colorRamp || 'YlOrRd',
            colorScheme: intent.visualization?.colorScheme || 'sequential',
          },
        ],
      }

    case 'filter':
      return {
        handled: true,
        pipeline: [
          {
            op: 'filter',
            dataset: intent.target?.dataset || 'countries',
            location: intent.target?.location || null,
            country: intent.target?.country || null,
            coordinates: intent.target?.coordinates || null,
            attribute: intent.operation?.attribute || 'POP_EST',
            action: intent.operation?.action || null,
            filterOperator: intent.operation?.filter?.operator || null,
            filterValue: intent.operation?.filter?.value || null,
            vizType: intent.visualization?.type || 'fill',
            colorRamp: intent.visualization?.colorRamp || 'plasma',
            colorScheme: intent.visualization?.colorScheme || 'sequential',
          },
        ],
      }

    case 'compare':
      return {
        handled: true,
        pipeline: [
          {
            op: 'compare',
            dataset: intent.target?.dataset || 'countries',
            countries: intent.operation?.filter?.value || null,
            attribute: intent.operation?.attribute || 'POP_EST',
            colorRamp: intent.visualization?.colorRamp || 'RdYlGn',
            colorScheme: intent.visualization?.colorScheme || 'diverging',
          },
        ],
      }

    case 'overture_layer':
      return {
        handled: true,
        pipeline: [
          {
            op: 'overture_layer',
            theme: intent.theme,
            action: intent.action || 'show',
            location: intent.target?.location || null,
            coordinates: intent.target?.coordinates || null,
            category: intent.category || null,
            speech: intent.speech || null,
          },
        ],
      }

    case 'water_layer':
      return {
        handled: true,
        pipeline: [
          {
            op: 'water_layer',
            sublayer: intent.sublayer,
            action: intent.action || 'show',
            name: intent.name || null,
            speech: intent.speech || null,
          },
        ],
      }

    case 'rivers':
      return {
        handled: true,
        pipeline: [
          {
            op: 'rivers',
            location: intent.target?.location || null,
            country: intent.target?.country || null,
            show_boundaries: intent.show_boundaries || false,
            show_capitals: intent.show_capitals || false,
            show_choropleth: intent.show_choropleth || false,
            speech: intent.speech || null,
          },
        ],
      }

    case 'h3_density':
      return {
        handled: true,
        pipeline: [
          {
            op: 'h3_density',
            category: intent.poi_category || 'hospital',
            country: intent.target?.country || null,
            location: intent.target?.location || null,
            colorRamp: intent.visualization?.colorRamp || 'YlOrRd',
            poiLabel: intent.poi_category || 'POI',
            h3Mode: intent.h3_mode || 'density',
            smooth: !!intent.smooth,
            _originalIntent: intent,
          },
        ],
      }

    case 'choropleth_style':
      return {
        handled: true,
        pipeline: [
          {
            op: 'choropleth_style',
            style: intent.style,
          },
        ],
      }

    // CL-165 (roadmap H3.2): pedagogical layer-stack control.
    case 'layer_control':
      return {
        handled: true,
        pipeline: [
          {
            op: 'layer_control',
            action: intent.action,
            layer: intent.layer,
            opacity: intent.opacity,
          },
        ],
      }

    case 'choropleth_scale':
      return {
        handled: true,
        pipeline: [
          {
            op: 'choropleth_scale',
            mode: intent.mode,
          },
        ],
      }

    case 'division_action':
      // A division_action carrying a POI category is fundamentally a
      // "how many <POI> in <admin unit/level>" question — route it to the
      // unified spatial-join op (poi_in_admin), which counts via ST_Contains
      // and renders the polygon + contained POIs at any ADM level. Pure
      // boundary displays (no poi_category) stay on the boundaries op.
      if (intent.poi_category) {
        return {
          handled: true,
          pipeline: [
            {
              op: 'poi_in_admin',
              category: intent.poi_category,
              country: intent.country || null,
              name: intent.name || null,
              admin_level: intent.admin_level || null,
              subtype: intent.subtype || null,
              parent_name: intent.parent_name || null,
              speech: intent.speech || null,
            },
          ],
        }
      }
      return {
        handled: true,
        pipeline: [
          {
            op: 'boundaries',
            action: intent.action || 'show_divisions',
            country: intent.country || null,
            subtype: intent.subtype || null,
            name: intent.name || null,
            poi_category: intent.poi_category || null,
            speech: intent.speech || null,
            admin_level: intent.admin_level || null,
            scope_division: intent.scope_division || null,
            parent_name: intent.parent_name || null,
            metric: intent.metric || null,
            show_capitals: intent.show_capitals || false,
          },
        ],
      }

    case 'routing':
      return {
        handled: true,
        pipeline: [
          {
            op: 'routing',
            routing_type: intent.routing_type || 'directions',
            routing_profile: intent.routing_profile || 'driving-car',
            routing_locations: intent.routing_locations || [],
            routing_coordinates: intent.routing_coordinates || null,
            routing_range_seconds: intent.routing_range_seconds || null,
          },
        ],
      }

    case 'geocode':
      return {
        handled: true,
        pipeline: [
          {
            op: 'geocode',
            query: intent.query || intent.address || intent.target?.location || null,
          },
        ],
      }

    case 'route':
      return {
        handled: true,
        pipeline: [
          {
            op: 'route',
            origin: intent.origin || null,
            destination: intent.destination || null,
            transportMode: intent.transportMode || 'car',
          },
        ],
      }

    case 'climate_zones':
      return {
        handled: true,
        pipeline: [
          {
            op: 'climate_zones',
            climate_class: intent.climate_class || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'timezones':
      return {
        handled: true,
        pipeline: [
          {
            op: 'timezones',
            place: intent.place || intent.location || intent.target?.location || null,
            offset: intent.offset != null ? intent.offset : null,
            dst: intent.dst === true,
            compare_cities: intent.compare_cities || null,
            source_hour: intent.source_hour != null ? intent.source_hour : null,
          },
        ],
      }

    case 'freedom_house':
      return {
        handled: true,
        pipeline: [
          {
            op: 'freedom_house',
            status_filter: intent.status_filter || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'fragile_states':
      return {
        handled: true,
        pipeline: [
          {
            op: 'fragile_states',
            category_filter: intent.category_filter || null,
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'faostat':
      return {
        handled: true,
        pipeline: [
          {
            op: 'faostat',
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'heritage_sites':
      return {
        handled: true,
        pipeline: [
          {
            op: 'heritage_sites',
            category_filter: intent.category_filter || null,
            endangered: intent.endangered || false,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    case 'languages':
      return {
        handled: true,
        pipeline: [
          {
            op: 'languages',
            family_filter: intent.family_filter || null,
            endangered: intent.endangered || false,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
            language_search: intent.language_search || null,
          },
        ],
      }

    case 'religions':
      return {
        handled: true,
        pipeline: [
          {
            op: 'religions',
            religion_filter: intent.religion_filter || null,
            diversity_mode: intent.diversity_mode || null,
            denomination: intent.denomination || null,
            state_religion_mode: intent.state_religion_mode || false,
            state_rel_filter: intent.state_rel_filter || null,
            population_mode: intent.population_mode || null,
            projection_mode: intent.projection_mode || null,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    case 'who_health':
      return {
        handled: true,
        pipeline: [
          {
            op: 'who_health',
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    case 'aquastat':
      return {
        handled: true,
        pipeline: [
          {
            op: 'aquastat',
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    case 'wto_trade':
      return {
        handled: true,
        pipeline: [
          {
            op: 'wto_trade',
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
            partner: intent.partner || null,
            compare: intent.compare || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    // 'physical_features' is the canonical whitelist spelling the Worker's
    // tool path emits since CL-143; 'physical_feature' remains the client
    // fast-path / registry-op dialect. Both normalize to the same op.
    case 'physical_feature':
    case 'physical_features':
      return {
        handled: true,
        pipeline: [
          {
            op: 'physical_feature',
            feature: intent.feature || null,
            name: intent.name || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'volcanoes':
      return {
        handled: true,
        pipeline: [
          {
            op: 'volcanoes',
            location: intent.location || intent.target?.location || null,
            volcano_type: intent.volcano_type || null,
            active_only: intent.active_only || false,
            recent_years: intent.recent_years || null,
            name: intent.name || null,
          },
        ],
      }

    case 'acled':
      return {
        handled: true,
        pipeline: [
          {
            op: 'acled',
            location: intent.location || intent.target?.location || null,
            event_type: intent.event_type || null,
            recent_days: intent.recent_days || null,
            min_fatalities: intent.min_fatalities || null,
          },
        ],
      }

    case 'noaa_climate':
      return {
        handled: true,
        pipeline: [
          {
            op: 'noaa_climate',
            sub_command: intent.sub_command || null,
            location: intent.location || intent.target?.location || null,
            current_type: intent.current_type || null,
            _rawQuery: intent._rawQuery || intent.speech || '',
          },
        ],
      }

    case 'terrain_analysis':
      return {
        handled: true,
        pipeline: [
          {
            op: 'terrain_analysis',
            metric: intent.metric || 'slope',
            location: intent.location || intent.target?.location || null,
            dem_layer: intent.dem_layer || null,
            // Boundary-clip hints (CL-225/CL-227): admin_level for ADM1–5,
            // country (ISO3) + parent_name to resolve/disambiguate a single
            // division, clip:false to force the full rectangle.
            admin_level: intent.admin_level || null,
            country: intent.country || null,
            parent_name: intent.parent_name || null,
            clip: intent.clip,
            speech: intent.speech || null,
            _rawQuery: intent._rawQuery || intent.speech || '',
          },
        ],
      }

    case 'usda_fas':
      return {
        handled: true,
        pipeline: [
          {
            op: 'usda_fas',
            commodity: intent.commodity || intent.indicator || null,
            attribute: intent.attribute || null,
            us_exports: intent.us_exports || false,
            location: intent.location || intent.target?.location || null,
            _rawQuery: intent._rawQuery || intent.speech || '',
          },
        ],
      }

    case 'food_security':
      return {
        handled: true,
        pipeline: [
          {
            op: 'food_security',
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
          },
        ],
      }

    case 'un_treaties':
      return {
        handled: true,
        pipeline: [
          {
            op: 'un_treaties',
            treaty: intent.treaty || null,
            indicator: intent.indicator || null,
            location: intent.location || intent.target?.location || null,
          },
        ],
      }

    case 'joshua_project':
      return {
        handled: true,
        pipeline: [
          {
            op: 'joshua_project',
            location: intent.location || intent.target?.location || null,
            count_query: intent.count_query || false,
            people_search: intent.people_search || null,
            religion_filter: intent.religion_filter || null,
            cluster_filter: intent.cluster_filter || null,
            indigenous: intent.indigenous || false,
            unreached: intent.unreached || false,
            language_filter: intent.language_filter || null,
          },
        ],
      }

    // ── Educational: falls through — handled by main.js streaming ──
    case 'educational':
      return { handled: false }

    // bivariate_choropleth uses DuckDB path, handled via sql routing
    case 'bivariate_choropleth':
      return { handled: false }

    // reset: falls through for now — minimal handler
    case 'reset':
      return { handled: false }

    // ── Not recognized: fall through to legacy router ──
    default:
      return { handled: false }
  }
}
